/**
 * Bird footprint glyph from Figma "Footprint" (used by Steps 781:109920 / 778:102174).
 * Path viewBox: 0 0 63.3426 64.1926. Display size in Steps: 154.939 × 157.018.
 */
export const BIRD_FOOTPRINT_PATH =
  'M29.1429 57.9678C28.5719 56.123 35.0264 56.5187 34.8451 58.3731C34.6636 60.2275 34.0249 63.4327 32.0726 64.127C30.1206 64.821 29.7139 59.8131 29.1429 57.9678ZM32.4086 7.37408C35.9631 7.36246 34.4592 14.6445 34.5736 23.3282C33.9389 35.5759 35.8126 37.1616 37.2884 41.6817C44.6548 37.4987 57.6874 29.3378 59.0394 31.3516C60.6735 33.7867 42.0278 42.9078 38.9056 49.0264C38.3762 50.0642 37.7988 50.855 37.1996 51.4454C35.9311 52.8909 33.9215 53.1489 32.4086 53.2774C31.8411 53.3256 31.2806 53.2908 30.7455 53.169C29.3036 53.1083 27.4455 52.0841 25.0326 49.0772C19.8021 42.559 3.36638 33.692 5.01599 31.2676C6.42199 29.2032 20.1428 38.0108 27.3383 42.1094C27.9692 37.8839 28.8362 30.0716 29.2582 22.8721C29.7521 14.4445 28.8543 7.38604 32.4086 7.37408ZM0.0765323 27.6534C0.449298 26.6378 2.59638 28.1459 3.5277 28.5323C4.45902 28.9187 2.14531 31.3783 1.4613 30.6866C0.777293 29.9948 -0.296125 28.6688 0.0765323 27.6534ZM59.8119 28.004C60.7458 27.6238 62.9029 26.1305 63.2689 27.1485C63.6347 28.1665 62.5522 29.4848 61.8636 30.1719C61.1751 30.859 58.8786 28.3845 59.8119 28.004ZM31.9271 0.46588C32.3266 -0.264025 32.8679 -0.0863222 33.1185 0.652403C33.4848 1.7351 33.8917 2.62248 33.892 3.17779C33.892 3.73313 31.3042 6.13721 31.1088 4.88092C30.9134 3.62466 31.1335 1.91573 31.9271 0.46588Z';

/** Document-level `<symbol>` id referenced by trail `<use href>`. */
export const BIRD_FOOTPRINT_GLYPH_ID = 'bird-footprint-glyph';

export const BIRD_FOOTPRINT_VIEWBOX_WIDTH = 63.3426;
export const BIRD_FOOTPRINT_VIEWBOX_HEIGHT = 64.1926;

/** Display size of each Footprint in Figma Steps. */
export const BIRD_FOOTPRINT_DISPLAY_WIDTH = 154.939;
export const BIRD_FOOTPRINT_DISPLAY_HEIGHT = 157.018;

/**
 * One footprint in a Steps trail — axis-aligned wrapper box + rotation.
 * Glyph is centred in the wrapper then rotated.
 */
export type BirdFootprintStep = {
  left: number;
  top: number;
  wrapperWidth: number;
  wrapperHeight: number;
  rotateDeg: number;
};

/**
 * Steps trail layout (Figma 781:109920). Stored top→bottom; BirdFootprints
 * staggers pops last→first (bottom→top of the walk). Used by Pressure / State.
 */
export const BIRD_FOOTPRINT_STEPS: readonly BirdFootprintStep[] = [
  {
    left: 49.59,
    top: 0,
    wrapperWidth: 178.755,
    wrapperHeight: 180.461,
    rotateDeg: -9.51,
  },
  {
    left: 0,
    top: 163.71,
    wrapperWidth: 220.32,
    wrapperHeight: 220.446,
    rotateDeg: -42.54,
  },
  {
    left: 184.61,
    top: 221.7,
    wrapperWidth: 183.367,
    wrapperHeight: 184.986,
    rotateDeg: -11.61,
  },
  {
    left: 97.66,
    top: 381.55,
    wrapperWidth: 218.667,
    wrapperHeight: 219.035,
    rotateDeg: -37.81,
  },
];

export const BIRD_FOOTPRINT_STEPS_VIEWBOX_WIDTH = 368;
export const BIRD_FOOTPRINT_STEPS_VIEWBOX_HEIGHT = 601;

/**
 * Marks Intro/2 prose so BirdFootprints can wait until that fade is discernible
 * before starting the footprint sequence.
 */
export const INTRO_PART2_FOOTPRINTS_READY_ATTR = 'data-intro-part2-footprints-ready';

export const INTRO_PART2_FOOTPRINTS_READY_SELECTOR = `[${INTRO_PART2_FOOTPRINTS_READY_ATTR}]`;

export const INTRO_PART2_FOOTPRINTS_READY_PROPS = {
  [INTRO_PART2_FOOTPRINTS_READY_ATTR]: '',
} as const;

/**
 * Marks the Pressure header so both footprint trails share one scroll-ready
 * clock (top-right, then lower-left).
 */
export const PRESSURE_FOOTPRINTS_READY_ATTR = 'data-pressure-footprints-ready';

export const PRESSURE_FOOTPRINTS_READY_SELECTOR = `[${PRESSURE_FOOTPRINTS_READY_ATTR}]`;

export const PRESSURE_FOOTPRINTS_READY_PROPS = {
  [PRESSURE_FOOTPRINTS_READY_ATTR]: '',
} as const;

/**
 * Whole-SVG transform for a footprint trail (viewBox units). Applied around
 * the Steps viewBox centre so individual feet keep their relative gait.
 */
export type BirdFootprintTrailTransform = {
  x: number;
  y: number;
  rotateDeg: number;
  /** Uniform scale of the whole trail (1 = default Steps size). */
  scale: number;
  /**
   * Mirror vertically after rotate (Figma `-scale-y` / flip). Used when a
   * Steps instance is flipped in the design file.
   */
  flipY?: boolean;
};

/**
 * Intro/2 left flanking gait — toes point down-right (~4–5 o’clock).
 * Edit x / y / rotateDeg / scale here; layout is `BIRD_FOOTPRINT_STEPS`.
 */
export const INTRO_PART2_FOOTPRINTS_LEFT: BirdFootprintTrailTransform = {
  x: -200,
  y: 160,
  rotateDeg: 140,
  scale: 1.3,
};

/**
 * Intro/2 right flanking gait — toes point up-right (~1–2 o’clock).
 * Edit x / y / rotateDeg / scale here; layout is `BIRD_FOOTPRINT_STEPS`.
 */
export const INTRO_PART2_FOOTPRINTS_RIGHT: BirdFootprintTrailTransform = {
  x: 200,
  y: 280,
  rotateDeg: 80,
  scale: 1.3,
};

/**
 * State / Red List Categories — trail on the right of the donut frame.
 * Figma UI Desktop `778:102174`: rotate 90° + scaleY(-1), AABB ≈ 356×211,
 * top ≈ 23 / right ≈ 59 within the 1440 Red List frame (`565:90597`).
 * Placement sizing lives in `RedListCategoriesFrame.module.css`.
 */
export const RED_LIST_CATEGORIES_FOOTPRINTS: BirdFootprintTrailTransform = {
  x: 0,
  y: 0,
  rotateDeg: 90,
  scale: 1,
  flipY: true,
};

/**
 * Pressure header trails (top-right + lower-left share this transform).
 * Edit x / y / rotateDeg / scale here; layout is `BIRD_FOOTPRINT_STEPS`.
 * rotateDeg is CSS/SVG (negative = counter-clockwise).
 */
export const PRESSURE_FOOTPRINTS: BirdFootprintTrailTransform = {
  x: 100,
  y: -660,
  rotateDeg: -100,
  scale: 1,
};

/**
 * Response / enabling-conditions intro — left of the centred heading.
 * Toes point down-right (~4 o’clock). rotateDeg is CSS/SVG (positive = clockwise).
 */
export const RESPONSE_ENABLERS_FOOTPRINTS: BirdFootprintTrailTransform = {
  x: -150,
  y: 0,
  rotateDeg: 125,
  scale: 2.4,
};

/**
 * Conclusion / “within reach” CTA — right of the centred content block.
 * rotateDeg is CSS/SVG (positive = clockwise).
 */
export const CONCLUSION_REACH_FOOTPRINTS: BirdFootprintTrailTransform = {
  x: -300,
  y: 600,
  rotateDeg: 135,
  scale: 1.9,
};
